import type { z } from 'zod'
import type { SortingState, VisibilityState } from '@tanstack/vue-table'
import type { ConsultationReportFiltersState } from '@/models/reports/consultationReport.js'
import type { InvoiceReportFiltersState } from '@/models/reports/invoiceReport.js'
import { omit as _omit } from 'lodash-es'

/**
 * Generic type for report filters state inferred from a Zod schema
 */
export type ReportFiltersState<TSchema extends z.ZodObject<z.ZodRawShape>> =
  z.infer<TSchema>

export type ReportTotals = {
  amount: number
  due: number
}

/**
 * Report ID constants for type-safe usage.
 * Add new report types here as they're implemented.
 */
type ReportStateIds = (typeof ReportStateId)[keyof typeof ReportStateId]
export const ReportStateId = {
  Consultations: 'consultations',
  Invoices: 'invoices',
} as const

/**
 * Type registry mapping report IDs to their state types.
 */
type ReportStateRegistry = {
  [ReportStateId.Consultations]: ConsultationReportFiltersState
  [ReportStateId.Invoices]: InvoiceReportFiltersState
}

/**
 * Valid report ID values (derived from ReportStateId const)
 */

/**
 * Generic composable for persisting report table state across route navigation.
 *
 * Supports multiple report types with isolated state storage.
 * State is stored in Nuxt's useState under 'report-states' and persists until page refresh.
 * Each report maintains its own state keyed by reportId.
 *
 * @param reportId - The report ID to manage state for
 * @param filtersSchema - Zod schema for validating filters
 *
 * @example
 * ```ts
 * import { invoiceReportFiltersBaseSchema } from '@/models/reports/invoiceReport.ts'
 *
 * const { state, hasPersistedState, saveReportState } = useReportFiltersState(
 *   ReportStateId.Invoice,
 *   invoiceReportFiltersBaseSchema
 * )
 *
 * // Apply persisted state on mount
 * onMounted(() => {
 *   if (hasPersistedState.value && persistedState.value) {
 *     tableState.onGlobalFilterChange(persistedState.value.filters)
 *     tableState.onColumnVisibilityChange({
 *       expand: false,
 *       ...persistedState.value.columnVisibility,
 *     })
 *     tableState.onSortingChange(persistedState.value.sorting)
 *   }
 * })
 *
 * // Persist before leaving
 * onBeforeRouteLeave(() => {
 *   saveReportState(tableState, { amount, due })
 * })
 * ```
 */

export const REPORTS_STATE_KEY = 'report-states'

export function useReportFiltersState<TReportId extends ReportStateIds>(
  reportId: TReportId,
  filtersSchema: z.ZodObject<z.ZodRawShape>,
) {
  type TState = ReportStateRegistry[TReportId & keyof ReportStateRegistry]

  const registryState = useState<Partial<ReportStateRegistry>>(
    REPORTS_STATE_KEY,
    () => ({}),
  )

  const state = computed<TState | null>({
    get: () => (registryState.value[reportId] ?? null) as TState | null,
    set: (newValue) => {
      if (newValue === null) {
        const { [reportId]: _, ...rest } = registryState.value
        registryState.value = rest
      } else {
        registryState.value = {
          ...registryState.value,
          [reportId]: newValue,
        }
      }
    },
  })

  /**
   * Check if we have persisted state for this report
   */
  const hasPersistedState = computed(() => state.value !== null)

  /**
   * Clear the persisted state for this report
   */
  function clearState() {
    state.value = null
  }

  /**
   * Get default state values, merging with current state
   */
  function getDefaultState() {
    return {
      filters: state.value?.filters ?? filtersSchema.parse({}),
      columnVisibility: state.value?.columnVisibility ?? {},
      sorting: state.value?.sorting ?? [],
      totals: state.value?.totals,
    } as TState
  }

  /**
   * Save filters to state
   */
  function setFilters(newFilters: Partial<TState['filters']>) {
    state.value = {
      ...getDefaultState(),
      filters: filtersSchema.parse(newFilters),
    } as TState
  }

  /**
   * Save column visibility to state
   */
  function setColumnVisibility(visibility: VisibilityState) {
    state.value = {
      ...getDefaultState(),
      columnVisibility: visibility,
    } as TState
  }

  /**
   * Save sorting to state
   */
  function setSorting(sorting: SortingState) {
    state.value = {
      ...getDefaultState(),
      sorting,
    } as TState
  }

  /**
   * Save totals to state
   */
  function setTotals(totals: ReportTotals) {
    state.value = {
      ...getDefaultState(),
      totals,
    } as TState
  }

  /**
   * Save complete report state from TanStack Table instance
   */
  function saveReportState(
    tableState: {
      globalFilter?: { value: unknown }
      columnVisibility: { value: VisibilityState }
      sorting: { value: Array<{ id: string; desc: boolean }> }
    },
    totals: ReportTotals,
  ) {
    const filters = tableState.globalFilter?.value
    const columnVisibility = _omit(tableState.columnVisibility.value, [
      'expand',
    ])

    state.value = {
      ...state.value,
      filters: filters ? filtersSchema.parse(filters) : filtersSchema.parse({}),
      columnVisibility,
      sorting: tableState.sorting.value,
      totals,
    } as TState
  }

  return {
    state,
    hasPersistedState,
    setFilters,
    setColumnVisibility,
    setSorting,
    setTotals,
    clearState,
    saveReportState,
  }
}

/**
 * Factory function to create report-specific middleware for clearing state.
 *
 * The middleware clears the report's state when entering the report section
 * from outside (determined by pathPattern).
 *
 * @param reportId - The report ID to clear state for
 * @param pathPattern - Path pattern to check (e.g., '/reports/invoices')
 *
 * @example
 * ```ts
 * // In invoice report pages
 * definePageMeta({
 *   middleware: [createReportStateMiddleware(ReportStateId.Invoice, '/reports/invoices')],
 * })
 * ```
 */
export function createReportStateMiddleware(
  reportId: ReportStateIds,
  pathPattern: string,
) {
  return (
    _to: ReturnType<typeof useRoute>,
    from: ReturnType<typeof useRoute>,
  ) => {
    if (from.path && !from.path.includes(pathPattern)) {
      const registryState =
        useState<Partial<ReportStateRegistry>>(REPORTS_STATE_KEY)
      // Guard against undefined state value
      if (!registryState.value) {
        return
      }
      const key = reportId as keyof ReportStateRegistry
      const { [key]: _, ...rest } = registryState.value
      registryState.value = rest
    }
  }
}
